import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, Rss } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

const socialLinks = [
  { icon: Facebook, href: 'https://www.facebook.com/paisleycommunity', label: 'Facebook' },
  { icon: Twitter, href: 'https://x.com/paisleyorguk', label: 'Twitter' },
  { icon: Instagram, href: 'https://www.instagram.com/paisleyscotland', label: 'Instagram' },
  { icon: Linkedin, href: 'https://www.linkedin.com/company/paisley-on-the-web/', label: 'LinkedIn' },
];

export const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <BrandLogo size="lg" variant="light" />
            <p className="text-slate-400 text-sm leading-relaxed">
              Connecting businesses with the heart of Paisley. Your platform for sponsorship,
              advertising, and community engagement.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-sky-400 transition-colors"
                  data-testid={`social-${label.toLowerCase()}`}
                  aria-label={label}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Home</Link></li>
              <li><Link to="/sponsors" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Sponsorship Packages</Link></li>
              <li><Link to="/events" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Events & News</Link></li>
              <li><Link to="/jobs" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Latest Jobs</Link></li>
              <li><Link to="/properties" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Properties to Let</Link></li>
              <li><Link to="/directory" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Business Directory</Link></li>
              <li><Link to="/news" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">News & Insights</Link></li>
              <li><Link to="/sponsors" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Become a Sponsor</Link></li>
              <li className="pt-2 flex items-center gap-3 text-xs">
                <Rss className="h-3.5 w-3.5 text-orange-400" />
                <span className="text-slate-500">RSS:</span>
                <a
                  href="/api/feed/news.xml"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-orange-400 transition-colors"
                  data-testid="rss-news-link"
                >
                  News
                </a>
                <span className="text-slate-700">·</span>
                <a
                  href="/api/feed/jobs.xml"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-orange-400 transition-colors"
                  data-testid="rss-jobs-link"
                >
                  Jobs
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  Paisley, Renfrewshire<br />Scotland, UK
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-sky-400 flex-shrink-0" />
                <a href="mailto:brian@paisley.org.uk" className="text-slate-400 hover:text-sky-400 text-sm break-all">
                  brian@paisley.org.uk
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-sky-400 flex-shrink-0" />
                <a href="tel:+447957166339" className="text-slate-400 hover:text-sky-400 text-sm">
                  07957 166339
                </a>
              </li>
              <li>
                <Link to="/contact" className="inline-flex items-center text-sky-400 hover:text-sky-300 text-sm font-medium" data-testid="footer-contact-link">
                  Contact form →
                </Link>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Business Hours</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex justify-between text-slate-400">
                <span>Monday - Friday</span>
                <span>9:00 - 17:00</span>
              </li>
              <li className="flex justify-between text-slate-400">
                <span>Saturday</span>
                <span>10:00 - 14:00</span>
              </li>
              <li className="flex justify-between text-slate-400">
                <span>Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-slate-800">
              <Link
                to="/admin/login"
                className="text-slate-500 hover:text-slate-400 text-xs transition-colors"
              >
                Admin Access
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center md:text-left">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Paisley Scotland. All rights reserved.
            </p>
            <span className="hidden sm:inline text-slate-700">·</span>
            <p className="text-slate-500 text-xs">
              Powered by{' '}
              <a
                href="https://instavibe.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 hover:text-sky-300 font-medium transition-colors"
                data-testid="powered-by-instavibe"
              >
                InstaVibe
              </a>
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-300 transition-colors" data-testid="footer-terms-link">
              Terms of Service
            </Link>
            <Link to="/contact" className="text-slate-500 hover:text-slate-300 transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
